/**
 * `/reports` — the six documents the workspace generates. Port of the demo's
 * report pane before anything was generated (`renderReports`, demo line
 * 2654): one card per report, with the question that builds it.
 *
 * Each card does both things the reader might want: **Open** goes straight to
 * the report, built from the live data; the quoted question asks the
 * assistant, which shows its generation steps in the chat and then opens the
 * same page. The demo only offered the second.
 *
 * @see src/versions/v1/modules/reports/ReportsScene.transform.ts
 * @see docs/logic/reports/overview.md
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { askOem } from '../../shared/assistant/ask';
import { reportCards, type ReportCardView } from './ReportsScene.transform';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]';

/** Demo 2653. The pane's standfirst before a report exists. */
const STANDFIRST =
  'Six documents the workspace builds from the live data: open one here, or ask the assistant ' +
  'for it and it will compile the report, open it, and give you the file.';

export function ReportsScene() {
  const cards = useMemo(() => reportCards(), []);
  return (
    <SurfacePageLayout width="full">
      <Commentable name="ReportsScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-6" as="div">
            <SurfacePageHeader
              kicker="Reports"
              title="Generated reports"
              description={STANDFIRST}
              actions={<HelpAnchor doc="reports/overview" />}
            />
            <AskStrip view="reports" />
            <Commentable name="ReportCards">
              <ul className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3" role="list">
                {cards.map((card) => (
                  <li key={card.kind} className="min-w-0">
                    <ReportCard card={card} />
                  </li>
                ))}
              </ul>
            </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** Demo 2654. One `.repopt` card. */
function ReportCard({ card }: { card: ReportCardView }) {
  return (
    <article
      className="flex h-full min-w-0 flex-col gap-2 rounded-2xl border p-4 transition-colors hover:border-[var(--accent)]"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className="mono rounded px-1.5 py-px text-[10px] font-semibold tracking-[0.06em]"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
        >
          {card.format.toUpperCase()}
        </span>
        <span className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          {card.downloadLabel}
        </span>
      </div>
      <h2 className="text-[14px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
        <Link to={card.path} className={`rounded hover:underline ${FOCUS_RING}`}>
          {card.title}
        </Link>
      </h2>
      <p className="text-[12px]" style={{ color: 'var(--ink-secondary)' }}>
        {card.desc}
      </p>
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
        <Link
          to={card.path}
          className={`inline-flex min-h-[32px] items-center gap-1 rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-opacity hover:opacity-90 ${FOCUS_RING}`}
          style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
          aria-label={`Open ${card.title}`}
        >
          Open <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
        <button
          type="button"
          onClick={() => askOem(card.prompt)}
          className={`inline-flex min-h-[32px] min-w-0 items-center gap-1.5 rounded-full border px-3 py-1 text-left text-[11.5px] transition-colors hover:bg-[var(--surface-1)] ${FOCUS_RING}`}
          style={{ borderColor: 'var(--hairline)', color: 'var(--accent)' }}
        >
          <Sparkles className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="min-w-0">{`“${card.prompt}”`}</span>
        </button>
      </div>
    </article>
  );
}
